import { getThemeMainColor } from "msTeamsUI/chartThemes/chartThemes";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import pl from "shared/messages/pl";
import { ModesFilter, SegmentsFilter, YearsFilter } from "../filters";
import { CompanyReportSectionConfig, Scope, SelectionTypeEnum } from "../models";
import { ReportSection } from "../ReportSection";
import { OtherParametersSubSection } from "./OtherParametersSubSection";

export interface OtherParametersSectionProps {
    intermediaryId: number;
    scope: Scope | undefined;
    sectionConfig: CompanyReportSectionConfig;
    isMobileView: boolean;
}

export const OtherParametersSection: React.FC<OtherParametersSectionProps> = (props) => {
    const queryClient = useQueryClient();

    const [years, setYears] = useState(props.sectionConfig.periodDefinition.yearList);
    const [segments, setSegments] = useState(props.sectionConfig.company.segmentDefinition?.segmentList ?? []);
    const [modes, setModes] = useState(props.sectionConfig.modeList);

    useEffect(() => {
        const init = async () => {
            const queryKeys = [`other_${props.sectionConfig.company.code}`];
            await Promise.all(queryKeys.map((x) => queryClient.invalidateQueries(x)));
        };

        init();
    }, [props.scope?.code]);

    let filters: JSX.Element[] = [];

    if ((props.sectionConfig.company.segmentDefinition?.segmentList ?? []).length > 0) {
        filters.push(
            <SegmentsFilter
                key={props.sectionConfig.company.code}
                segmentList={segments}
                onSelectedSegmentsChanged={(segmentList) => setSegments(segmentList)}
                isMobileView={props.isMobileView}
                atLeastOneAlwaysSelected
                multiple={props.sectionConfig.company.segmentDefinition?.selectionType === SelectionTypeEnum.MULTIPLE}
                prefix={props.sectionConfig.company.name}
            />
        );
    }

    if (props.sectionConfig.periodDefinition.yearList.length > 0) {
        filters.push(
            <YearsFilter
                key={"years-filter"}
                years={years}
                onSelectedYearsChanged={(years) => setYears(years)}
                isMobileView={props.isMobileView}
                multiple={props.sectionConfig.periodDefinition.selectionType === SelectionTypeEnum.MULTIPLE}
                atLeastOneAlwaysSelected={props.sectionConfig.periodDefinition.isRequired}
            />
        );
    }
    if (props.sectionConfig.modeList.length > 0) {
        filters.push(
            <ModesFilter
                key="modes"
                modes={modes}
                onSelectedModesChanged={(modes) => setModes(modes)}
                isMobileView={props.isMobileView}
            />
        );
    }

    const content = (
        <OtherParametersSubSection
            key={props.sectionConfig.company.code}
            intermediaryId={props.intermediaryId}
            scope={props.scope}
            company={props.sectionConfig.company}
            selectedSegments={segments.filter((segment) => segment.selected).map((segment) => segment.code)}
            selectedYears={years.filter((year) => year.selected).map((year) => year.code)}
            selectedMode={modes.find((mode) => mode.selected)?.code ?? ""}
        />
    );

    return (
        <ReportSection
            isMobileView={props.isMobileView}
            title={`${pl.view360Card.reports.otherParameters} ${props.sectionConfig.company.name}`}
            filters={filters}
            reportContent={content}
            isCollapsible={props.isMobileView}
            highlightColor={getThemeMainColor(props.sectionConfig.company.themeName)}
        />
    );
};
